import * as i4 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { InjectionToken, Injectable, EventEmitter, Component, ChangeDetectionStrategy, Inject, Output, Input, ViewChild, NgModule } from '@angular/core';
import * as i2 from '@ngrx/store';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { take } from 'rxjs/operators';
import { timer } from 'rxjs';
import * as i3 from '@tinymce/tinymce-angular';
import { EditorModule, TINYMCE_SCRIPT_SRC } from '@tinymce/tinymce-angular';
import * as i5 from '@ng-bootstrap/ng-bootstrap';
import { NgbPopoverModule } from '@ng-bootstrap/ng-bootstrap';

const ZERO_KEY = 48;
const NINE_KEY = 57;
const DECIMAL_POINT_KEY = 110;
const PERIOD_KEY = 190;
const BACKSPACE_KEY = 8;
const DELETE_KEY = 46;
const NUMPAD_ZERO_KEY = 96;
const NUMPAD_NINE_KEY = 105;
const LEFT_ARROW_KEY = 37;
const RIGHT_ARROW_KEY = 38;
const UP_ARROW_KEY = 39;
const DOWN_ARROW_KEY = 40;
const TAB_KEY = 9;
const MINUS_KEY = 189;
const NUMBAD_MINUS_KEY = 109;
const ESC_KEY = 27;

const TINYMCE_URL = new InjectionToken('TINYMCE_URL');
const WIRIS_URL = new InjectionToken('WIRIS_URL');
const MMS_URL = new InjectionToken('MMS_URL');
const TINYMCE = new InjectionToken('TINYMCE_SCRIPT_SRC');
const TINYMCE_EXTERNAL_PLUGINS = new InjectionToken('WIRTINYMCE_EXTERNAL_PLUGINSIS_URL');
const TINYMCE_TOOLBAR = new InjectionToken('TINYMCE_TOOLBAR');
const TINYMCE_MENUBAR = 'edit insert view format table tools';
const TINYMCE_PLUGINS = new InjectionToken('TINYMCE_PLUGINS');
const tinymceDefaultUrl = 'https://dle-cdn.mheducation.com/mhe/tinymce/6.8.2';
const wirisDefaultUrl = 'https://wiris-tinymce-qastg.assess.nonprod.mheducation.com/7.5.0.1392';
const wirisDefaultMathUrl = 'https://wiris-qastg.assess.nonprod.mheducation.com/7.6.0.6375';
const mmsDefaultUrl = 'https://mms-api-qastg.assess.nonprod.mheducation.com';
const tinymceExternalPluginsDefault = {
    tiny_mce_wiris: 'https://dle-cdn.mheducation.com/mhe/wiris/mathtype-tinymce6/plugin.min.js',
};

const getState = createFeatureSelector('tinymce');
const getTinymceConfig = createSelector(getState, (state) => state);

class TextUtilityService {
    stripHtmlTags(content) {
        const element = document.createElement('div');
        element.innerHTML = content;
        return element.innerText;
    }
    convertMathmlToSingleChar(content) {
        return content ? content.replace(/<math(.*?)<\/math>/g, ' ') : '';
    }
    isNumeric(event) {
        const eventKeyCode = event.keyCode || event.which;
        const allowedKeycodes = [
            BACKSPACE_KEY,
            LEFT_ARROW_KEY,
            RIGHT_ARROW_KEY,
            DELETE_KEY
        ];
        if (allowedKeycodes.includes(eventKeyCode) ||
            (eventKeyCode >= ZERO_KEY &&
                eventKeyCode <= NINE_KEY) ||
            (eventKeyCode >= NUMPAD_ZERO_KEY &&
                eventKeyCode <= NUMPAD_NINE_KEY) ||
            event.ctrlKey) {
            return true;
        }
        else {
            return false;
        }
    }
}
TextUtilityService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.0", ngImport: i0, type: TextUtilityService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
TextUtilityService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.2.0", ngImport: i0, type: TextUtilityService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.0", ngImport: i0, type: TextUtilityService, decorators: [{
            type: Injectable
        }] });

class RichTextEditorComponent {
    constructor(textUtilityService, _zone, tinymceUrl, wirisUrl, mmsUrl, tinymceToolbar, tinymceExternalPlugins, tinymcePlugins, store) {
        this.textUtilityService = textUtilityService;
        this._zone = _zone;
        this.tinymceUrl = tinymceUrl;
        this.wirisUrl = wirisUrl;
        this.mmsUrl = mmsUrl;
        this.tinymceToolbar = tinymceToolbar;
        this.tinymceExternalPlugins = tinymceExternalPlugins;
        this.tinymcePlugins = tinymcePlugins;
        this.store = store;
        this.editorKeyup = new EventEmitter();
        this.editorBlur = new EventEmitter();
        this.plainTextKeyup = new EventEmitter();
        this.pasteError = new EventEmitter();
        this.loadComplete = new EventEmitter();
        this.content = '';
        this.showResize = true;
        this.tinymce = window['tinymce'];
        this.insertmenu = 'link media image inserttable | charmap hr';
        this.hasInitializedEditor = false;
        this.store
            .select(getTinymceConfig)
            .pipe(take(1))
            .subscribe((tinymceConfig) => {
            if (tinymceConfig) {
                if (tinymceConfig.tinymceUrl) {
                    this.tinymceUrl = tinymceConfig.tinymceUrl;
                }
                else {
                    this.tinymceUrl = tinymceDefaultUrl;
                }
                if (tinymceConfig.wirisUrl) {
                    this.wirisUrl = tinymceConfig.wirisUrl;
                }
                if (tinymceConfig.mmsUrl) {
                    this.mmsUrl = tinymceConfig.mmsUrl;
                }
                if (tinymceConfig.tinymceExternalPlugins) {
                    if (this.tinymceUrl === tinymceDefaultUrl) {
                        this.tinymceExternalPlugins = tinymceExternalPluginsDefault;
                    }
                    else {
                        this.tinymceExternalPlugins = tinymceConfig.tinymceExternalPlugins;
                    }
                }
            }
        });
    }
    ngAfterViewInit() {
        if (this.wirisUrl) {
            this.tinymce.wirisUrl = this.wirisUrl;
        }
        this.tinymce.mmsUrl = this.mmsUrl;
        this.tinymce.init({
            base_url: `${this.tinymceUrl}`,
            suffix: '.min',
            theme_url: `${this.tinymceUrl}/themes/silver/theme.min.js`,
            icons_url: `${this.tinymceUrl}/icons/default/icons.js`,
            model_url: `${this.tinymceUrl}/models/dom/model.js`,
            skin: `oxide`,
            branding: false,
            entity_encoding: 'raw',
            target: this.textarea.nativeElement,
            external_plugins: this.getExternalPlugins(),
            browser_spellcheck: true,
            menubar: this.getMenuBar(),
            toolbar: this.getToolbar(),
            setup: (editor) => this.setup(editor),
            file_picker_types: 'file image media',
            file_picker_callback: this.filePickerCallback.bind(this),
            images_upload_handler: this.imageUploadHandler.bind(this),
            default_link_target: '_blank',
            target_list: false,
            image_title: true,
            anchor_top: false,
            anchor_bottom: false,
            elementpath: this.elementPath,
            a11y_advanced_options: true,
            resize: this.showResize,
            paste_data_images: false,
            extended_valid_elements: '*[.*]',
            // We recommend to set 'draggable_modal' to true to avoid overlapping issues
            // with the different UI modal dialog windows implementations between core and third-party plugins on TinyMCE.
            // @see: https://github.com/wiris/html-integrations/issues/134#issuecomment-905448642
            draggable_modal: true,
            menu: {
                insert: { title: 'Insert', items: this.insertmenu },
            },
            table_header_type: 'sectionCells',
        });
    }
    ngOnDestroy() {
        this.tinymce.remove(this.editor);
    }
    ngOnChanges(changes) {
        if (changes.content && this.hasInitializedEditor && this.content !== this.editor.getContent()) {
            this.updateEditorContent();
        }
    }
    filePickerCallback(cb, value, meta) {
        {
            const input = document.createElement('input');
            input.setAttribute('type', 'file');
            input.setAttribute('id', 'files');
            if (meta.filetype === 'file') {
                input.setAttribute('accept', '.txt');
            }
            else if (meta.filetype === 'image') {
                input.setAttribute('accept', 'image/*');
            }
            else if (meta.filetype === 'media') {
                input.setAttribute('accept', 'audio/mpeg');
            }
            input.addEventListener('change', (e) => {
                const file = e.target.files[0];
                const reader = new FileReader();
                reader.addEventListener('load', () => {
                    const id = 'blobid' + new Date().getTime();
                    const blobCache = this.tinymce.activeEditor.editorUpload.blobCache;
                    const base64 = reader.result.split(',')[1];
                    const blobInfo = blobCache.create(id, file, base64);
                    blobCache.add(blobInfo);
                    if (meta.filetype === 'media') {
                        this.editor.editorUpload.media = file;
                    }
                    else {
                        this.editor.editorUpload.file = file; // for image and transcript
                    }
                    cb(blobInfo.blobUri(), { title: file.name });
                });
                reader.readAsDataURL(file);
            });
            input.click();
        }
    }
    updateEditorContent() {
        this.editor.setContent(this.content || '');
    }
    setup(editor) {
        this.editor = editor;
        this.setupListener('init', () => this.onEditorInit());
        this.setupListener('keyup change', () => this.onEditorChange());
        this.setupListener('keydown', (event) => this.onEditorKeydown(event));
        this.setupListener('paste', (event) => this.onEditorPaste(event));
    }
    onEditorInit() {
        this.loadComplete.emit(true);
        this.updateEditorContent();
        this.hasInitializedEditor = true;
        return true;
    }
    onEditorChange() {
        const editor = this.editor;
        const richContent = editor.getContent();
        const plainTextContent = editor.getContent({ format: 'text' });
        if (richContent === this.content || richContent === this.lastEmittedContent || this.isOverLimit()) {
            return false;
        }
        this.editorKeyup.emit(editor);
        this.plainTextKeyup.emit(plainTextContent);
        this.lastEmittedContent = richContent;
        return true;
    }
    onEditorKeydown(event) {
        this.selectedContent = this.editor.selection.getContent({ format: 'text' }) || '';
        return !this.isCharacterLimitReached() || this.isAllowedKeydownOverLimit(event);
    }
    onEditorPaste(event) {
        const copiedContent = (window['clipboardData'] && window['clipboardData'].getData('Text')) ||
            event.clipboardData.getData('Text');
        const copiedContentLength = this.lengthIgnoringHtmlTags(copiedContent);
        const editorContentLength = this.lengthIgnoringHtmlTags(this.editor.getContent());
        const selectedContentLength = this.lengthIgnoringHtmlTags(this.selectedContent);
        const lengthAfterPaste = copiedContentLength + editorContentLength - selectedContentLength;
        if (lengthAfterPaste > this.maxCharacter) {
            this.pasteError.emit();
            return false;
        }
        return true;
    }
    focusOnEditor() {
        this.editor.focus();
    }
    highlightContainer(hasError) {
        const editor = this.editor;
        if (editor && editor.editorContainer) {
            editor.editorContainer.style.borderColor = hasError ? '#b0181e' : '#c5c5c5';
        }
    }
    setupListener(events, callback) {
        this.editor.on(events, (event) => {
            let result = false;
            this._zone.run(() => {
                result = callback(event);
            });
            return result;
        });
    }
    isOverLimit() {
        return this.lengthIgnoringHtmlTags(this.editor.getContent()) > this.maxCharacter;
    }
    isCharacterLimitReached() {
        return this.lengthIgnoringHtmlTags(this.editor.getContent()) > this.maxCharacter - 1;
    }
    lengthIgnoringHtmlTags(content) {
        const textUtilityService = this.textUtilityService;
        const modifiedContent = textUtilityService.convertMathmlToSingleChar(content);
        const plainTextContent = textUtilityService.stripHtmlTags(modifiedContent);
        return plainTextContent.length;
    }
    isAllowedKeydownOverLimit(event) {
        const eventKeyCode = event.keyCode || event.which;
        const allowedKeycodes = [
            BACKSPACE_KEY,
            DELETE_KEY,
            LEFT_ARROW_KEY,
            RIGHT_ARROW_KEY,
            UP_ARROW_KEY,
        ];
        return allowedKeycodes.includes(eventKeyCode) || event.ctrlKey;
    }
    getExternalPlugins() {
        const mhePlugins = {
            skipReadspeaker: `${this.tinymceUrl}/plugins/skipReadspeaker/plugin.min.js`,
            charmap: `${this.tinymceUrl}/plugins/charmap/plugin.min.js`,
            advlist: `${this.tinymceUrl}/plugins/advlist/plugin.min.js`,
        };
        if (!this.noMedia) {
            mhePlugins.media = `${this.tinymceUrl}/plugins/media/plugin.min.js`;
        }
        const defaultPlugins = [
            'link',
            'table',
            'lists',
            'code',
            'fullscreen',
            'searchreplace',
            `charmap`,
            'image',
        ];
        for (const plugin of defaultPlugins) {
            mhePlugins[plugin] = `${this.tinymceUrl}/plugins/${plugin}/plugin.min.js`;
        }
        if (this.tinymceExternalPlugins) {
            mhePlugins.tiny_mce_wiris = this.tinymceExternalPlugins.tiny_mce_wiris;
        }
        if (this.tinymcePlugins?.length) {
            for (const plugin of this.tinymcePlugins) {
                mhePlugins[plugin] = `${this.tinymceUrl}/plugins/${plugin}/plugin.min.js`;
            }
        }
        return mhePlugins;
    }
    getToolbar() {
        const basicToolbar = 'undo redo | formatselect | bold italic underline';
        const baseToolbar = 'undo redo | searchreplace fontsizeselect bold italic underline strikethrough' +
            ' superscript subscript | forecolor backcolor | bullist numlist | alignleft aligncenter alignright' +
            ' alignjustify | outdent indent | table ';
        let toolbar = `${baseToolbar} media image `;
        if (this.noMedia) {
            toolbar = baseToolbar;
            this.insertmenu = this.insertmenu.replace('media image ', '');
        }
        if (this.imageOnly) {
            toolbar = `${baseToolbar} image `;
            this.insertmenu = this.insertmenu.replace('media ', '');
        }
        toolbar = this.tinymceExternalPlugins ? toolbar + 'tiny_mce_wiris_formulaEditor' : toolbar;
        toolbar = toolbar + ' charmap link code fullscreen skipReadspeaker';
        return this.basicMode ? basicToolbar : this.tinymceToolbar || toolbar;
    }
    getMenuBar() {
        return this.basicMode ? '' : this.tinymceMenubar || TINYMCE_MENUBAR;
    }
    imageUploadHandler(blobInfo, progress) {
        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.withCredentials = false;
            xhr.open('POST', `${this.mmsUrl}/api/v1/media/image/en-us`);
            xhr.upload.onprogress = (e) => {
                progress((e.loaded / e.total) * 100);
            };
            xhr.onload = () => {
                if (xhr.status === 403) {
                    reject({ message: 'HTTP Error: ' + xhr.status, remove: true });
                    return;
                }
                if (xhr.status !== 409 && (xhr.status < 200 || xhr.status >= 300)) {
                    reject('HTTP Error: ' + xhr.status);
                    return;
                }
                const json = JSON.parse(xhr.responseText);
                if (!json || typeof json.url !== 'string') {
                    reject('Invalid JSON: ' + xhr.responseText);
                    return;
                }
                resolve(json.url);
            };
            xhr.onerror = () => {
                reject('Image upload failed due to a XHR Transport error. Code: ' + xhr.status);
            };
            const formData = new FormData();
            formData.append('file', blobInfo.blob(), blobInfo.filename());
            xhr.send(formData);
        });
    }
}
RichTextEditorComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.0", ngImport: i0, type: RichTextEditorComponent, deps: [{ token: TextUtilityService }, { token: i0.NgZone }, { token: TINYMCE_URL }, { token: WIRIS_URL }, { token: MMS_URL }, { token: TINYMCE_TOOLBAR }, { token: TINYMCE_EXTERNAL_PLUGINS }, { token: TINYMCE_PLUGINS }, { token: i2.Store }], target: i0.ɵɵFactoryTarget.Component });
RichTextEditorComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.2.0", type: RichTextEditorComponent, selector: "mhe-rich-text-editor", inputs: { content: "content", maxCharacter: "maxCharacter", noMedia: "noMedia", imageOnly: "imageOnly", toggleRichTextMenuBar: "toggleRichTextMenuBar", elementPath: "elementPath", basicMode: "basicMode", tinymceMenubar: "tinymceMenubar", showResize: "showResize" }, outputs: { editorKeyup: "editorKeyup", editorBlur: "editorBlur", plainTextKeyup: "plainTextKeyup", pasteError: "pasteError", loadComplete: "loadComplete" }, viewQueries: [{ propertyName: "textarea", first: true, predicate: ["textarea"], descendants: true }], usesOnChanges: true, ngImport: i0, template: `<textarea #textarea></textarea>`, isInline: true, styles: [":host ::ng-deep [aria-label=\"Font Sizes\"] button{width:65px}@media only screen and (max-width: 580px){::ng-deep .wrs_modal_dialogContainer{left:0!important;top:0!important;overflow:auto!important;height:256px!important;width:320px!important}::ng-deep .wrs_header{overflow:scroll!important}}\n"], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.0", ngImport: i0, type: RichTextEditorComponent, decorators: [{
            type: Component,
            args: [{ selector: 'mhe-rich-text-editor', template: `<textarea #textarea></textarea>`, changeDetection: ChangeDetectionStrategy.OnPush, styles: [":host ::ng-deep [aria-label=\"Font Sizes\"] button{width:65px}@media only screen and (max-width: 580px){::ng-deep .wrs_modal_dialogContainer{left:0!important;top:0!important;overflow:auto!important;height:256px!important;width:320px!important}::ng-deep .wrs_header{overflow:scroll!important}}\n"] }]
        }], ctorParameters: function () { return [{ type: TextUtilityService }, { type: i0.NgZone }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [TINYMCE_URL]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [WIRIS_URL]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [MMS_URL]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [TINYMCE_TOOLBAR]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [TINYMCE_EXTERNAL_PLUGINS]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [TINYMCE_PLUGINS]
                }] }, { type: i2.Store }]; }, propDecorators: { editorKeyup: [{
                type: Output
            }], editorBlur: [{
                type: Output
            }], plainTextKeyup: [{
                type: Output
            }], pasteError: [{
                type: Output
            }], loadComplete: [{
                type: Output
            }], content: [{
                type: Input
            }], maxCharacter: [{
                type: Input
            }], noMedia: [{
                type: Input
            }], imageOnly: [{
                type: Input
            }], toggleRichTextMenuBar: [{
                type: Input
            }], elementPath: [{
                type: Input
            }], basicMode: [{
                type: Input
            }], tinymceMenubar: [{
                type: Input
            }], showResize: [{
                type: Input
            }], textarea: [{
                type: ViewChild,
                args: ['textarea']
            }] } });

const PASTE_ERROR_MESSAGE = 'The content exceeds the character limit.';

class EditorComponent {
    constructor(textUtilityService) {
        this.textUtilityService = textUtilityService;
        this.contentChange = new EventEmitter();
        this.pasteError = new EventEmitter();
        this.escape = new EventEmitter();
        this.editorFocusOut = new EventEmitter();
        this.useRichText = true;
        this.noMedia = false;
        this.imageOnly = false;
        this.toggleRichTextMenuBar = true;
        this.elementPath = true;
        this.basicMode = false;
        this.showCharacterLimit = true;
        this.useA11yTooltip = false;
        this.errors = [];
        this.showResize = true;
        this.render = true;
        this.richValue = '';
        this.plainValue = '';
        this.pasteErrorMessage = PASTE_ERROR_MESSAGE;
        this.characterCount = 0;
        this.hasPasteError = false;
    }
    ngOnInit() {
        this.plainValue = this.plainValue || this.content;
        this.setRichValue(this.richValue || this.content);
    }
    ngOnChanges() {
        this.plainValue = this.content;
        this.setRichValue(this.content);
        this.highlightContainer();
    }
    onEditorBlur() {
        this.editorFocusOut.emit(this.content);
    }
    onRichTextEditorKeyup(editor) {
        this.setRichValue(editor.getContent());
        if (editor.hasMath) {
            editor.hasMath = false;
            this.rerender();
        }
        this.emitCurrentValue();
        this.hasPasteError = false;
    }
    onRichTextEditorPlainTextKeyup(value) {
        this.plainValue = value;
    }
    onPlainTextKeyup(value) {
        this.plainValue = value;
        this.richValue = value;
        this.characterCount = this.plainValue.length;
        this.emitCurrentValue();
        this.hasPasteError = false;
    }
    setRichValue(value) {
        this.richValue = value;
        this.characterCount = this.lengthIgnoringHtmlTags(value);
    }
    emitCurrentValue() {
        this.useRichText ? this.contentChange.emit(this.richValue) : this.contentChange.emit(this.plainValue);
    }
    lengthIgnoringHtmlTags(content) {
        const textUtilityService = this.textUtilityService;
        const modifiedContent = textUtilityService.convertMathmlToSingleChar(content);
        const plainTextContent = textUtilityService.stripHtmlTags(modifiedContent);
        return plainTextContent.length;
    }
    checkCharacterCount(showCharacterText) {
        const percentage = showCharacterText ? 100 : 10;
        return this.maxCharacter - this.characterCount <= this.maxCharacter / percentage;
    }
    onPasteError() {
        this.pasteError.emit();
        this.hasPasteError = true;
        this.highlightContainer();
    }
    onTextAreaPaste(e) {
        const data = e.clipboardData.getData('Text');
        if (!this.maxCharacter) {
            return true;
        }
        const isLongerThanMaxCharLength = data.length + this.lengthIgnoringHtmlTags(e.target.value) > this.maxCharacter;
        if (isLongerThanMaxCharLength) {
            this.onPasteError();
            return false;
        }
        return true;
    }
    rerender() {
        this.render = false;
        timer(0).subscribe(() => (this.render = true));
    }
    onEscape() {
        this.escape.emit();
    }
    skipToEditor(event) {
        event.preventDefault();
        this.richTextEditorComponent.focusOnEditor();
    }
    get showErrors() {
        return this.pasteErrorMessage && this.hasPasteError
            ? this.errors.concat(this.pasteErrorMessage)
            : this.errors;
    }
    highlightContainer() {
        if (this.richTextEditorComponent && Array.isArray(this.showErrors)) {
            this.richTextEditorComponent.highlightContainer(!!this.showErrors.length);
        }
    }
}
EditorComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.0", ngImport: i0, type: EditorComponent, deps: [{ token: TextUtilityService }], target: i0.ɵɵFactoryTarget.Component });
EditorComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.2.0", type: EditorComponent, selector: "mhe-editor", inputs: { maxCharacter: "maxCharacter", characterText: "characterText", content: "content", useRichText: "useRichText", noMedia: "noMedia", imageOnly: "imageOnly", toggleRichTextMenuBar: "toggleRichTextMenuBar", tinymceMenubar: "tinymceMenubar", elementPath: "elementPath", basicMode: "basicMode", showCharacterLimit: "showCharacterLimit", useA11yTooltip: "useA11yTooltip", errors: "errors", showResize: "showResize" }, outputs: { contentChange: "contentChange", pasteError: "pasteError", escape: "escape", editorFocusOut: "editorFocusOut" }, viewQueries: [{ propertyName: "richTextEditorComponent", first: true, predicate: RichTextEditorComponent, descendants: true }], usesOnChanges: true, ngImport: i0, template: "<div class=\"a11y-tooltip\" *ngIf=\"useA11yTooltip\">\n  <button\n    class=\"btn btn-outline-primary mb-3 d-flex align-items-center justify-content-center\"\n    ngbPopover=\"Press ALT + F10 to access the toolbar from the input field. Press Escape to access the input field from\n        the toolbar.\"\n    placement=\"bottom auto\"\n  >\n    <i class=\"bi bi-info-circle pe-2\"></i>Keyboard Help\n  </button>\n</div>\n\n<div\n  *ngIf=\"showCharacterLimit\"\n  class=\"character-count\"\n  [ngClass]=\"{\n    'below-character-limit': checkCharacterCount(characterText),\n    'question-text': characterText,\n    'plain-text': !useRichText\n  }\"\n>\n  {{ maxCharacter - characterCount }}/{{ maxCharacter }}\n  <span class=\"character-text\" *ngIf=\"characterText\">{{ characterText }}</span>\n</div>\n<div class=\"ahe-ui form-group has-feedback\" [ngClass]=\"{ 'has-error': showErrors.length }\">\n  <div *ngIf=\"useRichText\">\n    <mhe-rich-text-editor\n      *ngIf=\"render\"\n      [content]=\"richValue\"\n      [noMedia]=\"noMedia\"\n      [imageOnly]=\"imageOnly\"\n      [maxCharacter]=\"maxCharacter\"\n      [basicMode]=\"basicMode\"\n      [toggleRichTextMenuBar]=\"toggleRichTextMenuBar\"\n      [tinymceMenubar]=\"tinymceMenubar\"\n      [showResize]=\"showResize\"\n      [elementPath]=\"elementPath\"\n      (editorKeyup)=\"onRichTextEditorKeyup($event)\"\n      (editorBlur)=\"onEditorBlur()\"\n      (pasteError)=\"onPasteError()\"\n      (plainTextKeyup)=\"onRichTextEditorPlainTextKeyup($event)\"\n      (escape)=\"onEscape()\"\n    ></mhe-rich-text-editor>\n\n    <mhe-rich-text-editor\n      *ngIf=\"!render\"\n      [content]=\"richValue\"\n      [noMedia]=\"noMedia\"\n      [imageOnly]=\"imageOnly\"\n      [maxCharacter]=\"maxCharacter\"\n      [basicMode]=\"basicMode\"\n      [toggleRichTextMenuBar]=\"toggleRichTextMenuBar\"\n      [tinymceMenubar]=\"tinymceMenubar\"\n      [elementPath]=\"elementPath\"\n      [showResize]=\"showResize\"\n      (editorKeyup)=\"onRichTextEditorKeyup($event)\"\n      (editorBlur)=\"onEditorBlur()\"\n      (pasteError)=\"onPasteError()\"\n      (plainTextKeyup)=\"onRichTextEditorPlainTextKeyup($event)\"\n      (escape)=\"onEscape()\"\n    ></mhe-rich-text-editor>\n  </div>\n\n  <textarea\n    *ngIf=\"!useRichText\"\n    class=\"plain-text-editor\"\n    [ngClass]=\"{ 'plain-text-editor-error': showErrors.length }\"\n    (input)=\"onPlainTextKeyup($event.target.value)\"\n    (blur)=\"onEditorBlur()\"\n    (paste)=\"onTextAreaPaste($event)\"\n    [attr.maxlength]=\"maxCharacter || null\"\n    [value]=\"plainValue || ''\"\n  ></textarea>\n  <span class=\"help-block\">\n    <span *ngFor=\"let error of showErrors\">{{ error }} <br /></span>\n  </span>\n</div>\n<editor apiKey=\"hfxwbitjz5igoywk56dehmaga6so5brhep8wml4vybi7ggip\" style=\"display: none\"></editor>\n", styles: [".plain-text-editor{width:100%;resize:vertical}.character-count{position:relative;top:-10px}.character-count.plain-text{top:0}.character-count.below-character-limit{color:#b0181e}.a11y-tooltip{margin-bottom:10px}.plain-text-editor-error{border:1px solid #b0181e;outline:none}.tooltip-btn{background:0 0;margin-left:-10px}::ng-deep .tox-promotion{display:none}\n"], components: [{ type: RichTextEditorComponent, selector: "mhe-rich-text-editor", inputs: ["content", "maxCharacter", "noMedia", "imageOnly", "toggleRichTextMenuBar", "elementPath", "basicMode", "tinymceMenubar", "showResize"], outputs: ["editorKeyup", "editorBlur", "plainTextKeyup", "pasteError", "loadComplete"] }, { type: i3.EditorComponent, selector: "editor", inputs: ["cloudChannel", "apiKey", "init", "id", "initialValue", "outputFormat", "inline", "tagName", "plugins", "toolbar", "modelEvents", "allowedEvents", "ignoreEvents", "disabled"] }], directives: [{ type: i4.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i5.NgbPopover, selector: "[ngbPopover]", inputs: ["animation", "autoClose", "ngbPopover", "popoverTitle", "placement", "triggers", "container", "disablePopover", "popoverClass", "openDelay", "closeDelay"], outputs: ["shown", "hidden"], exportAs: ["ngbPopover"] }, { type: i4.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { type: i4.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.0", ngImport: i0, type: EditorComponent, decorators: [{
            type: Component,
            args: [{ selector: 'mhe-editor', changeDetection: ChangeDetectionStrategy.OnPush, template: "<div class=\"a11y-tooltip\" *ngIf=\"useA11yTooltip\">\n  <button\n    class=\"btn btn-outline-primary mb-3 d-flex align-items-center justify-content-center\"\n    ngbPopover=\"Press ALT + F10 to access the toolbar from the input field. Press Escape to access the input field from\n        the toolbar.\"\n    placement=\"bottom auto\"\n  >\n    <i class=\"bi bi-info-circle pe-2\"></i>Keyboard Help\n  </button>\n</div>\n\n<div\n  *ngIf=\"showCharacterLimit\"\n  class=\"character-count\"\n  [ngClass]=\"{\n    'below-character-limit': checkCharacterCount(characterText),\n    'question-text': characterText,\n    'plain-text': !useRichText\n  }\"\n>\n  {{ maxCharacter - characterCount }}/{{ maxCharacter }}\n  <span class=\"character-text\" *ngIf=\"characterText\">{{ characterText }}</span>\n</div>\n<div class=\"ahe-ui form-group has-feedback\" [ngClass]=\"{ 'has-error': showErrors.length }\">\n  <div *ngIf=\"useRichText\">\n    <mhe-rich-text-editor\n      *ngIf=\"render\"\n      [content]=\"richValue\"\n      [noMedia]=\"noMedia\"\n      [imageOnly]=\"imageOnly\"\n      [maxCharacter]=\"maxCharacter\"\n      [basicMode]=\"basicMode\"\n      [toggleRichTextMenuBar]=\"toggleRichTextMenuBar\"\n      [tinymceMenubar]=\"tinymceMenubar\"\n      [showResize]=\"showResize\"\n      [elementPath]=\"elementPath\"\n      (editorKeyup)=\"onRichTextEditorKeyup($event)\"\n      (editorBlur)=\"onEditorBlur()\"\n      (pasteError)=\"onPasteError()\"\n      (plainTextKeyup)=\"onRichTextEditorPlainTextKeyup($event)\"\n      (escape)=\"onEscape()\"\n    ></mhe-rich-text-editor>\n\n    <mhe-rich-text-editor\n      *ngIf=\"!render\"\n      [content]=\"richValue\"\n      [noMedia]=\"noMedia\"\n      [imageOnly]=\"imageOnly\"\n      [maxCharacter]=\"maxCharacter\"\n      [basicMode]=\"basicMode\"\n      [toggleRichTextMenuBar]=\"toggleRichTextMenuBar\"\n      [tinymceMenubar]=\"tinymceMenubar\"\n      [elementPath]=\"elementPath\"\n      [showResize]=\"showResize\"\n      (editorKeyup)=\"onRichTextEditorKeyup($event)\"\n      (editorBlur)=\"onEditorBlur()\"\n      (pasteError)=\"onPasteError()\"\n      (plainTextKeyup)=\"onRichTextEditorPlainTextKeyup($event)\"\n      (escape)=\"onEscape()\"\n    ></mhe-rich-text-editor>\n  </div>\n\n  <textarea\n    *ngIf=\"!useRichText\"\n    class=\"plain-text-editor\"\n    [ngClass]=\"{ 'plain-text-editor-error': showErrors.length }\"\n    (input)=\"onPlainTextKeyup($event.target.value)\"\n    (blur)=\"onEditorBlur()\"\n    (paste)=\"onTextAreaPaste($event)\"\n    [attr.maxlength]=\"maxCharacter || null\"\n    [value]=\"plainValue || ''\"\n  ></textarea>\n  <span class=\"help-block\">\n    <span *ngFor=\"let error of showErrors\">{{ error }} <br /></span>\n  </span>\n</div>\n<editor apiKey=\"hfxwbitjz5igoywk56dehmaga6so5brhep8wml4vybi7ggip\" style=\"display: none\"></editor>\n", styles: [".plain-text-editor{width:100%;resize:vertical}.character-count{position:relative;top:-10px}.character-count.plain-text{top:0}.character-count.below-character-limit{color:#b0181e}.a11y-tooltip{margin-bottom:10px}.plain-text-editor-error{border:1px solid #b0181e;outline:none}.tooltip-btn{background:0 0;margin-left:-10px}::ng-deep .tox-promotion{display:none}\n"] }]
        }], ctorParameters: function () { return [{ type: TextUtilityService }]; }, propDecorators: { contentChange: [{
                type: Output
            }], pasteError: [{
                type: Output
            }], escape: [{
                type: Output
            }], editorFocusOut: [{
                type: Output
            }], maxCharacter: [{
                type: Input
            }], characterText: [{
                type: Input
            }], content: [{
                type: Input
            }], useRichText: [{
                type: Input
            }], noMedia: [{
                type: Input
            }], imageOnly: [{
                type: Input
            }], toggleRichTextMenuBar: [{
                type: Input
            }], tinymceMenubar: [{
                type: Input
            }], elementPath: [{
                type: Input
            }], basicMode: [{
                type: Input
            }], showCharacterLimit: [{
                type: Input
            }], useA11yTooltip: [{
                type: Input
            }], errors: [{
                type: Input
            }], showResize: [{
                type: Input
            }], richTextEditorComponent: [{
                type: ViewChild,
                args: [RichTextEditorComponent]
            }] } });

class RichTextEditorModule {
    static forRoot(config) {
        return {
            ngModule: RichTextEditorModule,
            providers: [
                {
                    provide: TINYMCE_URL,
                    useValue: config.tinymceUrl,
                },
                {
                    provide: WIRIS_URL,
                    useValue: config.wirisUrl,
                },
                {
                    provide: MMS_URL,
                    useValue: config.mmsUrl,
                },
                {
                    provide: TINYMCE_EXTERNAL_PLUGINS,
                    useValue: config.tinymceExternalPlugins,
                },
                {
                    provide: TINYMCE_TOOLBAR,
                    useValue: config.tinymceToolbar,
                },
                {
                    provide: TINYMCE_PLUGINS,
                    useValue: config.tinymcePlugins,
                },
            ],
        };
    }
}
RichTextEditorModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.0", ngImport: i0, type: RichTextEditorModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
RichTextEditorModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.2.0", ngImport: i0, type: RichTextEditorModule, declarations: [RichTextEditorComponent, EditorComponent], imports: [CommonModule, EditorModule, NgbPopoverModule], exports: [RichTextEditorComponent, EditorComponent] });
RichTextEditorModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.2.0", ngImport: i0, type: RichTextEditorModule, providers: [
        { provide: TINYMCE_SCRIPT_SRC, useValue: 'tinymce/tinymce.min.js' },
        TextUtilityService,
    ], imports: [[CommonModule, EditorModule, NgbPopoverModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.0", ngImport: i0, type: RichTextEditorModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [RichTextEditorComponent, EditorComponent],
                    imports: [CommonModule, EditorModule, NgbPopoverModule],
                    providers: [
                        { provide: TINYMCE_SCRIPT_SRC, useValue: 'tinymce/tinymce.min.js' },
                        TextUtilityService,
                    ],
                    exports: [RichTextEditorComponent, EditorComponent],
                }]
        }] });

class TinymceModule {
}
TinymceModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.0", ngImport: i0, type: TinymceModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
TinymceModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.2.0", ngImport: i0, type: TinymceModule, imports: [CommonModule, RichTextEditorModule, NgbPopoverModule], exports: [RichTextEditorModule] });
TinymceModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.2.0", ngImport: i0, type: TinymceModule, providers: [], imports: [[CommonModule, RichTextEditorModule, NgbPopoverModule], RichTextEditorModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.0", ngImport: i0, type: TinymceModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [],
                    imports: [CommonModule, RichTextEditorModule, NgbPopoverModule],
                    exports: [RichTextEditorModule],
                    providers: [],
                }]
        }] });

const LoadTinymceConfig = '[Tinymce Config] Load Tinymce Config';
class LoadTinymceConfigAction {
    constructor(payload) {
        this.payload = payload;
        this.type = LoadTinymceConfig;
    }
}

const initialState = {
    tinymceUrl: null,
    wirisUrl: null,
    mmsUrl: null,
    tinymceSkinsUrl: null,
    tinymceExternalPlugins: null,
    hideA11yChecker: false
};
function TinymceConfigReducer(state = initialState, action) {
    switch (action.type) {
        case LoadTinymceConfig:
            return { ...action.payload };
        default:
            return state;
    }
}

class TinymceConfig {
    constructor(d) {
        this.tinymceUrl = d.tinmceUrl;
        this.wirisUrl = d.wirisUrl;
        this.mmsUrl = d.mmsUrl;
        this.tinymceSkinsUrl = d.tinymceSkinsUrl;
        this.tinymceExternalPlugins = d.tinymceExternalPlugins;
        this.hideA11yChecker = d.hideA11yChecker;
    }
}

/*
 * Public API Surface of tinymce
 */
// These should go to a /testing secondary entry point

/**
 * Generated bundle index. Do not edit.
 */

export { EditorComponent, LoadTinymceConfigAction, RichTextEditorComponent, RichTextEditorModule, TinymceConfig, TinymceConfigReducer, TinymceModule };
